import { useState } from "react";
import { Plus, Trash2, CheckCircle2, XCircle, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import { PasteDialog } from "@/components/PasteDialog";
import { Input } from "@/components/ui/input";
import { DateInput, parseDMY } from "@/components/DateInput";
import { minExpiry, gap } from "@/lib/passport";
import { GROUP } from "@/constants/testIds";

const newRow = () => ({ key: Math.random().toString(36).slice(2), name: "", exp: "" });

const Status = ({ dep, exp }) => {
  if (!dep || !exp) return <span className="text-xs text-slate-400">—</span>;
  const valid = exp >= minExpiry(dep);
  const Icon = valid ? CheckCircle2 : XCircle;
  return (
    <div className="flex flex-col">
      <span className={`inline-flex items-center gap-1.5 text-sm font-bold ${valid ? "text-emerald-600" : "text-red-600"}`} data-testid={GROUP.rowStatus}>
        <Icon className="h-4 w-4" />{valid ? "VALID" : "EXPIRED"}
      </span>
      <span className="text-xs text-slate-500">{gap(dep, exp)}</span>
    </div>
  );
};

const Row = ({ row, index, dep, onChange, onRemove, canRemove }) => {
  const exp = parseDMY(row.exp);
  const invalid = row.exp.length === 10 && !exp;
  return (
    <li className="grid grid-cols-[2rem_1fr_auto] items-center gap-3 px-4 py-3 sm:grid-cols-[2rem_1fr_12rem_9rem_2.5rem]" data-testid={GROUP.row}>
      <span className="text-xs text-slate-400 tabular-nums">{index + 1}</span>
      <Input value={row.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Nama Peserta (opsional)" className="h-11 bg-white" data-testid={GROUP.rowName} />
      <button onClick={onRemove} disabled={!canRemove} className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 sm:order-last" aria-label="Hapus baris" data-testid={GROUP.rowRemove}>
        <Trash2 className="h-4 w-4" />
      </button>
      <div className="col-span-2 col-start-2 sm:col-span-1 sm:col-start-auto">
        <DateInput value={row.exp} onChange={(v) => onChange({ exp: v })} invalid={invalid} className="h-11 bg-white tabular-nums tracking-[0.1em]" data-testid={GROUP.rowExpiry} />
      </div>
      <div className="col-span-2 col-start-2 sm:col-span-1 sm:col-start-auto">
        <Status dep={dep} exp={exp} />
      </div>
    </li>
  );
};

export const GroupCheck = ({ dep }) => {
  const [rows, setRows] = useState(() => [newRow(), newRow(), newRow()]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const applyPaste = (parsed) => {
    setRows((rs) => [...rs.filter((r) => r.name.trim() || r.exp), ...parsed.map((p) => ({ ...newRow(), ...p }))]);
    toast.success(`${parsed.length} peserta dimasukkan`);
  };
  const checked = dep ? rows.map((r) => parseDMY(r.exp)).filter(Boolean) : [];
  const valid = checked.filter((e) => e >= minExpiry(dep)).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={GROUP.summary}>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{checked.length} dicek</span>
        <span className="rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700" data-testid={GROUP.validCount}>{valid} VALID</span>
        <span className="rounded-full bg-red-50 px-3 py-1 font-medium text-red-700" data-testid={GROUP.expiredCount}>{checked.length - valid} EXPIRED</span>
        {!dep && <span className="text-xs text-slate-500">Isi tanggal keberangkatan terlebih dahulu.</span>}
      </div>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        <li className="hidden grid-cols-[2rem_1fr_12rem_9rem_2.5rem] gap-3 px-4 py-2.5 sm:grid">
          <span className="overline-label">#</span><span className="overline-label">Nama Peserta</span>
          <span className="overline-label">Expired paspor</span><span className="overline-label">Status</span><span />
        </li>
        {rows.map((r, i) => (
          <Row key={r.key} row={r} index={i} dep={dep} canRemove={rows.length > 1}
            onChange={(p) => update(r.key, p)} onRemove={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} />
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setPasteOpen(true)} className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark transition-colors" data-testid={GROUP.pasteButton}>
          <ClipboardPaste className="h-4 w-4" /> Tempel daftar
        </button>
        <button onClick={() => setRows((rs) => [...rs, newRow()])} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-slate-300 hover:text-brand transition-colors" data-testid={GROUP.addRow}>
          <Plus className="h-4 w-4" /> Tambah paspor
        </button>
        <button onClick={() => setRows([newRow(), newRow(), newRow()])} className="rounded-full px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid={GROUP.clear}>
          Kosongkan daftar
        </button>
      </div>
      <PasteDialog open={pasteOpen} onOpenChange={setPasteOpen} onApply={applyPaste} />
    </div>
  );
};
